import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  TextField, Typography, MenuItem, Checkbox, FormControlLabel,
  IconButton, Divider, CircularProgress, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';
import warrantyTicketServices from '../services/warrantyTicketServices';

const WARRANTY_TYPES = [
  { value: 'stockUnit', label: 'Stock Unit' },
  { value: 'standard', label: 'Standard' },
  { value: 'bulletin', label: 'Bulletin' },
  { value: 'serviceParts', label: 'Service Parts' },
  { value: 'competitiveEquipment', label: 'Competitive Equipment' },
  { value: 'preauthorization', label: 'Preauthorization' },
  { value: 'unitExchange', label: 'Unit Exchange' },
  { value: 'optContract', label: 'Opt. Contract (parts only)' },
];

const APPLICATION_TYPES = [
  { value: 'ownerOccupied', label: 'Owner Occupied Residential' },
  { value: 'otherResidential', label: 'Other Residential Application' },
  { value: 'commercial', label: 'Commercial' },
];

const MODEL_LOCATIONS = [
  { value: 'attic', label: 'Attic' },
  { value: 'basement', label: 'Basement' },
  { value: 'carportGarage', label: 'Carport/Garage' },
  { value: 'crawlSpace', label: 'Crawl Space' },
  { value: 'closet', label: 'Closet' },
  { value: 'outdoors', label: 'Outdoors' },
  { value: 'rooftop', label: 'Rooftop' },
];

const GAS_FUELS = [
  { value: 'propane', label: 'Propane' },
  { value: 'naturalGas', label: 'Natural Gas' },
];

const ORIENTATIONS = [
  { value: 'downflow', label: 'Downflow' },
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'upflow', label: 'Upflow' },
];

const EMPTY_PART = {
  causal: false,
  failedPart: '', failedQty: '1', failedInvoice: '', failedSn: '', failedInstallDate: '',
  replacedPart: '', replacedQty: '1', replacedInvoice: '', replacedSn: '', replacedInstallDate: '',
};

const LAST_EMAIL_KEY = 'sca_last_recipient';

export default function ScaFormDialog({ open, onClose, ticket }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !ticket?.id) return;
    setForm(null);
    setLoading(true);
    warrantyTicketServices.scaPrefill(ticket.id)
      .then((res) => {
        const data = res.data || {};
        if (!data.parts || data.parts.length === 0) {
          data.parts = [{ ...EMPTY_PART, causal: true }];
        }
        setForm(data);
      })
      .catch((e) => {
        console.error(e);
        toast.error('Failed to load SCA prefill data');
        setForm({ parts: [{ ...EMPTY_PART, causal: true }] });
      })
      .finally(() => setLoading(false));
    setEmailTo(localStorage.getItem(LAST_EMAIL_KEY) || '');
    setEmailCc('');
    setEmailMessage('');
  }, [open, ticket?.id]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const setPart = (idx, field, value) => {
    setForm((f) => {
      const parts = [...(f.parts || [])];
      parts[idx] = { ...parts[idx], [field]: value };
      return { ...f, parts };
    });
  };

  const addPart = () => {
    setForm((f) => ({ ...f, parts: [...(f.parts || []), { ...EMPTY_PART }] }));
  };

  const removePart = (idx) => {
    setForm((f) => ({ ...f, parts: (f.parts || []).filter((_, i) => i !== idx) }));
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await warrantyTicketServices.scaPdf(ticket.id, form);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate PDF');
    }
    setPreviewing(false);
  };

  const handleSend = async () => {
    const to = emailTo.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (to.length === 0) {
      toast.error('Recipient email is required');
      return;
    }
    const cc = emailCc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    setSending(true);
    try {
      await warrantyTicketServices.scaSend(ticket.id, {
        form,
        to,
        cc: cc.length ? cc : undefined,
        message: emailMessage || undefined,
      });
      localStorage.setItem(LAST_EMAIL_KEY, emailTo);
      toast.success('SCA claim sent');
      onClose(true);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Failed to send SCA claim');
    }
    setSending(false);
  };

  const partRowField = (idx, field, label, width = 1) => (
    <TextField
      size="small"
      label={label}
      value={form.parts[idx][field] || ''}
      onChange={(e) => setPart(idx, field, e.target.value)}
      sx={{ gridColumn: `span ${width}` }}
    />
  );

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Sigler SCA — Warranty Claim
        {ticket?.ticketNumber && <Chip size="small" label={ticket.ticketNumber} />}
      </DialogTitle>
      <DialogContent dividers>
        {loading || !form ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Dealer / claim header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              <TextField size="small" label="Claim # (after 100599-)" value={form.claimNumber || ''} onChange={set('claimNumber')} />
              <TextField size="small" label="Customer #" value={form.customerNumber || ''} onChange={set('customerNumber')} />
              <TextField size="small" label="Distributor #" value={form.distributorNumber || ''} onChange={set('distributorNumber')} />
              <TextField size="small" label="Dealer Name" value={form.dealerName || ''} onChange={set('dealerName')} />
            </Box>

            <Divider textAlign="left"><Typography variant="caption" sx={{ fontWeight: 700 }}>CUSTOMER INFORMATION</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              <TextField size="small" label="First Name" value={form.firstName || ''} onChange={set('firstName')} />
              <TextField size="small" label="Last Name" value={form.lastName || ''} onChange={set('lastName')} />
              <TextField size="small" label="Street" value={form.street || ''} onChange={set('street')} sx={{ gridColumn: 'span 2' }} />
              <TextField size="small" label="City" value={form.city || ''} onChange={set('city')} />
              <TextField size="small" label="State" value={form.state || ''} onChange={set('state')} />
              <TextField size="small" label="Zip" value={form.zip || ''} onChange={set('zip')} />
              <TextField size="small" label="Phone" value={form.phone || ''} onChange={set('phone')} />
              <TextField size="small" label="Email" value={form.email || ''} onChange={set('email')} sx={{ gridColumn: 'span 2' }} />
            </Box>

            <Divider textAlign="left"><Typography variant="caption" sx={{ fontWeight: 700 }}>CLAIM TYPE</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              <TextField select size="small" label="Warranty Type" value={form.warrantyType || ''} onChange={set('warrantyType')}>
                <MenuItem value="">—</MenuItem>
                {WARRANTY_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Application Type" value={form.applicationType || ''} onChange={set('applicationType')}>
                <MenuItem value="">—</MenuItem>
                {APPLICATION_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Installing Homeowner" value={form.installingHomeowner || ''} onChange={set('installingHomeowner')}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Box>

            <Divider textAlign="left"><Typography variant="caption" sx={{ fontWeight: 700 }}>UNIT INFORMATION</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
              <TextField size="small" label="Model #" value={form.modelNumber || ''} onChange={set('modelNumber')} sx={{ gridColumn: { md: 'span 2' } }} />
              <TextField size="small" label="Serial #" value={form.serialNumber || ''} onChange={set('serialNumber')} sx={{ gridColumn: { md: 'span 2' } }} />
              <Box />
              <TextField size="small" label="Install Date" placeholder="M/D/YYYY" value={form.installDate || ''} onChange={set('installDate')} />
              <TextField size="small" label="Failed Date" placeholder="M/D/YYYY" value={form.failedDate || ''} onChange={set('failedDate')} />
              <TextField size="small" label="Repair Date" placeholder="M/D/YYYY" value={form.repairDate || ''} onChange={set('repairDate')} />
            </Box>

            <Divider textAlign="left"><Typography variant="caption" sx={{ fontWeight: 700 }}>PARTS (max 5)</Typography></Divider>
            {(form.parts || []).map((part, idx) => (
              <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!part.causal}
                        onChange={(e) => setPart(idx, 'causal', e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Causal part</Typography>}
                  />
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => removePart(idx)} disabled={(form.parts || []).length <= 1}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 0.6fr 1fr 1fr 1fr' }, gap: 1 }}>
                  {partRowField(idx, 'failedPart', 'Failed: Carrier Part #')}
                  {partRowField(idx, 'failedQty', 'Qty')}
                  {partRowField(idx, 'failedInvoice', 'Order/Invoice #')}
                  {partRowField(idx, 'failedSn', 'Part SN')}
                  {partRowField(idx, 'failedInstallDate', 'Install Date')}
                  {partRowField(idx, 'replacedPart', 'Replaced: Carrier Part #')}
                  {partRowField(idx, 'replacedQty', 'Qty')}
                  {partRowField(idx, 'replacedInvoice', 'Order/Invoice #')}
                  {partRowField(idx, 'replacedSn', 'Part SN')}
                  {partRowField(idx, 'replacedInstallDate', 'Install Date')}
                </Box>
              </Box>
            ))}
            {(form.parts || []).length < 5 && (
              <Button size="small" startIcon={<AddIcon />} onClick={addPart} sx={{ alignSelf: 'flex-start' }}>
                Add part row
              </Button>
            )}

            <Divider textAlign="left"><Typography variant="caption" sx={{ fontWeight: 700 }}>QUALITY INFORMATION</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              <TextField select size="small" label="Model Location" value={form.modelLocation || ''} onChange={set('modelLocation')}>
                <MenuItem value="">—</MenuItem>
                {MODEL_LOCATIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Furnace Gas Fuel" value={form.gasFuel || ''} onChange={set('gasFuel')}>
                <MenuItem value="">—</MenuItem>
                {GAS_FUELS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Furnace Orientation" value={form.furnaceOrientation || ''} onChange={set('furnaceOrientation')}>
                <MenuItem value="">—</MenuItem>
                {ORIENTATIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Box>

            <TextField
              size="small" multiline minRows={2} label="Service Performed"
              value={form.servicePerformed || ''} onChange={set('servicePerformed')}
            />
            <TextField
              size="small" multiline minRows={1} label="New Comment"
              value={form.newComment || ''} onChange={set('newComment')}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 1.5 }}>
              <TextField size="small" label="Service Provider (signature)" value={form.providerSignature || ''} onChange={set('providerSignature')} />
              <TextField size="small" label="Date" value={form.signatureDate || ''} onChange={set('signatureDate')} />
            </Box>

            <Divider textAlign="left"><Typography variant="caption" sx={{ fontWeight: 700 }}>EMAIL</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                size="small" label="To (Sigler warranty email)" required
                value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="warranty@sigler.com"
              />
              <TextField
                size="small" label="CC"
                value={emailCc} onChange={(e) => setEmailCc(e.target.value)}
              />
            </Box>
            <TextField
              size="small" multiline minRows={2} label="Message (optional)"
              value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={previewing ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          disabled={!form || previewing}
          onClick={handlePreview}
        >
          Preview PDF
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={!form || sending}
          onClick={handleSend}
        >
          Send Claim
        </Button>
      </DialogActions>
    </Dialog>
  );
}
